import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import {
  MdArrowDropDown,
  MdArrowDropUp,
  MdDragIndicator,
} from "react-icons/md";
import { flexRender } from "@tanstack/react-table";
import { FROZEN_COLUMN_INDEXES } from "./hooks/useColumnOrder";
import { useSwingLibraryStore } from "../../store/swingLibraryStore";
import useOverflowCheck from "../../../student_credits_manage/hooks/useOverflowCheck";
import { Tooltip } from "../../../../components/shared/Tooltip/Tooltip";

function normalizeSortField(field) {
  return field === "videoCreated" ? "videoPath" : field;
}

function SortIcon({ direction }) {
  const Icon = direction === "desc" ? MdArrowDropDown : MdArrowDropUp;
  return <Icon className="size-5 min-w-5" />;
}

/** fixed width constraints per accessor */
const COL_SIZE = {
  tags: { maxWidth: "181px", minWidth: "64px" },
  "userData.fullName": { maxWidth: "181px", minWidth: "112px" },
  "userData.golfClub": { width: "64px" },
  videoPath: { width: "100px" },
};

export default function SortableHeader({ columnIndex, header }) {
  const { setSort, sort } = useSwingLibraryStore();

  const [hoverSortField, setHoverSortField] = useState(null);
  const [isResized, setIsResized] = useState(false);

  const accessorKey = header.column.columnDef?.accessorKey ?? header.column.id;
  const headerLabel = header.column.columnDef.header;

  /** DnD — disable for frozen columns */
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      disabled: columnIndex === 0, // keep your original rule
      id: header.id,
    });

  /** ellipsis tooltip */
  const { isOverflowing, tagRef, textRef } = useOverflowCheck({
    dependency: headerLabel,
  });

  /** click to sort */
  const handleSort = (key) => {
    const currentField = normalizeSortField(sort?.sort);
    const order =
      currentField === key ? (sort?.order === "asc" ? "desc" : "asc") : "desc";

    switch (key) {
      case "userData.fullName":
        setSort({ order, sort: "userData.fullName" });
        break;
      case "userData.golfClub":
        setSort({ order, sort: "userData.golfClub" });
        break;
      case "videoPath":
        setSort({ order, sort: "videoCreated" });
        break;
    }
  };

  /** decide which arrow to show */
  const sortMeta = useMemo(() => {
    const currentField = normalizeSortField(sort?.sort);
    const hoverField = normalizeSortField(hoverSortField);
    const activeField = hoverField || currentField;

    return {
      currentField,
      direction: sort?.order || "desc",
      isActive: activeField === accessorKey,
    };
  }, [sort, hoverSortField, accessorKey]);

  const showDragHandle = !FROZEN_COLUMN_INDEXES.includes(columnIndex);

  const sizeConfig = (accessorKey ? COL_SIZE[accessorKey] : undefined) ?? {};
  const ariaSort =
    normalizeSortField(sort?.sort) === accessorKey
      ? sort?.order === "asc"
        ? "ascending"
        : "descending"
      : undefined;

  return (
    <th
      aria-sort={ariaSort}
      className={`bg-gray50 text-gray500 relative py-3 text-left select-none ${
        showDragHandle ? "pr-2" : "px-2"
      }`}
      ref={setNodeRef}
      style={{
        maxWidth: !isResized && sizeConfig.maxWidth ? sizeConfig.maxWidth : "",
        minWidth: sizeConfig.minWidth ? sizeConfig.minWidth : 100,
        opacity: isDragging ? 0.8 : 1,
        transform: transform ? CSS.Transform.toString(transform) : undefined,
        width:
          !isResized && sizeConfig.width ? sizeConfig.width : header.getSize(),
        zIndex: isDragging ? 10 : 5,
      }}
    >
      {!header.isPlaceholder && (
        <div
          className="flex h-full max-h-[33px] items-center justify-start overflow-hidden"
          ref={tagRef}
        >
          {showDragHandle && (
            <MdDragIndicator
              className="size-5 min-w-5 cursor-grab outline-none"
              {...attributes}
              {...listeners}
            />
          )}

          <Tooltip
            content={
              isOverflowing ? (
                <span className="text-sm text-white">
                  {flexRender(headerLabel, header.getContext())}
                </span>
              ) : null
            }
            placement="top"
          >
            <span
              className="flex cursor-pointer items-center gap-1"
              onClick={() => handleSort(accessorKey)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleSort(accessorKey);
              }}
              onMouseEnter={() => setHoverSortField(accessorKey)}
              onMouseLeave={() => setHoverSortField(null)}
              role="button"
              tabIndex={0}
            >
              <span
                className="line-clamp-2 text-xs text-ellipsis whitespace-normal"
                ref={textRef}
              >
                {flexRender(headerLabel, header.getContext())}
              </span>

              {sortMeta.isActive ? (
                <SortIcon direction={sortMeta.direction} />
              ) : null}
            </span>
          </Tooltip>

          {/* Column resizer */}
          <div
            className={
              `bg-gray200 absolute top-0 right-0 h-full w-px cursor-col-resize touch-none select-none hover:w-1` +
              (header.column.getIsResizing() ? "bg-gray300 w-1" : "")
            }
            onDoubleClick={() => {
              setIsResized(false);
              header.column.resetSize();
            }}
            onMouseDown={(e) => {
              setIsResized(true);
              header.getResizeHandler()(e);
            }}
            onTouchStart={(e) => {
              setIsResized(true);
              header.getResizeHandler()(e);
            }}
          />
        </div>
      )}
    </th>
  );
}
